/** Every live report of a project: what it draws, how often the link was opened, where it is. */
import { useState } from 'react';
import { Badge, Button, Card, Group, Switch, Table, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { useDeleteReport, usePatchReport, type Report } from '@/api/reports';
import { CellText } from '@/components/CellText';
import { rowClick, rowStyle } from '@/components/rowClick';
import { NAME_MAX_WIDTH, NAME_MIN_WIDTH } from '@/components/tableWidths';
import { SortableTh } from '@/components/SortableTh';
import { useTableSort } from '@/components/useTableSort';
import { useFormat } from '@/components/useFormat';
import { CommentCount } from '@/features/staff/InternalComments';
import { brand } from '@/theme/theme';
import { ReportSettingsDrawer } from './ReportSettingsDrawer';
import { ConfirmModal, copyText } from './ShareLinkCard';

export interface ReportsTableProps {
  projectId: string;
  reports: Report[];
  /** Staff open the staff page, which carries the bindings and the revocation buttons. */
  staff?: boolean;
  /** Internal comments per report id (P16); staff only. */
  commentCounts?: Record<string, number>;
}

/** Show or hide a report and delete it. Clicks never bubble: the modal is a portal, but React events still climb to the row. */
function StaffRowActions({ report }: { report: Report }) {
  const { t } = useTranslation('reports');
  const patch = usePatchReport(report.public_id);
  const remove = useDeleteReport();
  const [confirming, setConfirming] = useState(false);

  const toggle = (visible: boolean) => {
    patch
      .mutateAsync({ visible_to_client: visible })
      .then(() => notifications.show({ color: 'forest', message: t(visible ? 'table.shown' : 'table.hiddenNow') }))
      .catch((e: unknown) => notifications.show({ color: 'red', message: errorMessage(e) }));
  };
  const confirmDelete = () => {
    remove
      .mutateAsync(report.public_id)
      .then(() => {
        setConfirming(false);
        notifications.show({ color: 'forest', message: t('share.deleted') });
      })
      .catch((e: unknown) => notifications.show({ color: 'red', message: errorMessage(e) }));
  };

  return (
    <span onClick={(e) => e.stopPropagation()}>
      <Group gap="xs" wrap="nowrap">
        <Switch
          size="sm"
          label={t('table.visible')}
          checked={report.visible_to_client}
          disabled={patch.isPending}
          onChange={(e) => toggle(e.currentTarget.checked)}
        />
        <Button size="xs" variant="subtle" color="red" onClick={() => setConfirming(true)}>
          {t('table.delete')}
        </Button>
      </Group>
      <ConfirmModal
        opened={confirming}
        title={t('share.deleteTitle')}
        description={t('share.deleteHint')}
        loading={remove.isPending}
        onConfirm={confirmDelete}
        onClose={() => setConfirming(false)}
      />
    </span>
  );
}

export function ReportsTable({ projectId, reports, staff = false, commentCounts }: ReportsTableProps) {
  const { t } = useTranslation('reports');
  const { date, number } = useFormat();
  const navigate = useNavigate();
  const { sorted, sortProps } = useTableSort(reports, {
    name: (r) => r.name || t(`kinds.${r.kind}`),
    kind: (r) => t(`kinds.${r.kind}`),
    views: (r) => r.view_count ?? 0,
    created: (r) => Date.parse(r.created_at),
  });

  const copy = (report: Report) => {
    void copyText(report.link, null).then((copied) => {
      if (copied) notifications.show({ color: 'forest', message: t('share.copied') });
    });
  };

  return (
    <Card p={0}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <SortableTh {...sortProps('name')}>{t('table.name')}</SortableTh>
            <SortableTh {...sortProps('kind')}>{t('table.kind')}</SortableTh>
            <SortableTh {...sortProps('views')}>{t('table.views')}</SortableTh>
            <SortableTh {...sortProps('created')}>{t('table.created')}</SortableTh>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sorted.map((r) => (
            <Table.Tr
              key={r.public_id}
              style={rowStyle}
              onClick={rowClick(() =>
                void navigate(
                  staff
                    ? { to: '/staff/reports/$reportId', params: { reportId: r.public_id } }
                    : { to: '/projects/$projectId/reports/$reportId', params: { projectId, reportId: r.public_id } },
                ),
              )}
            >
              <Table.Td>
                <Group gap={6} wrap="nowrap" align="flex-start">
                  <CellText lines={2} fz={14} fw={600} miw={NAME_MIN_WIDTH} maw={NAME_MAX_WIDTH} title={r.name || t(`kinds.${r.kind}`)}>
                    {staff ? (
                      <Link to="/staff/reports/$reportId" params={{ reportId: r.public_id }} style={{ color: brand.text }}>
                        {r.name || t(`kinds.${r.kind}`)}
                      </Link>
                    ) : (
                      <Link to="/projects/$projectId/reports/$reportId" params={{ projectId, reportId: r.public_id }} style={{ color: brand.text }}>
                        {r.name || t(`kinds.${r.kind}`)}
                      </Link>
                    )}
                  </CellText>
                  {staff && <CommentCount count={commentCounts?.[r.public_id]} />}
                </Group>
              </Table.Td>
              <Table.Td>
                <Group gap="xs">
                  <Text fz={14}>{t(`kinds.${r.kind}`)}</Text>
                  {r.render_state.gone && (
                    <Badge size="xs" color="gray">
                      {t('table.gone')}
                    </Badge>
                  )}
                </Group>
              </Table.Td>
              <Table.Td>{number(r.view_count ?? 0)}</Table.Td>
              <Table.Td>{date(r.created_at)}</Table.Td>
              <Table.Td>
                <Group gap="xs" wrap="nowrap">
                  <Button size="xs" variant="default" onClick={() => copy(r)} disabled={!r.visible_to_client}>
                    {t('share.copy')}
                  </Button>
                  {staff && <ReportSettingsDrawer report={r} />}
                  {staff && <StaffRowActions report={r} />}
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  );
}
